import { ChangeDetectorRef, Component, Input, Output, EventEmitter, OnInit, OnChanges, SimpleChanges, AfterViewInit, ViewEncapsulation } from '@angular/core';
import { forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { ManagedataService } from 'src/app/providers/input/managedata/managedata.service';
import { AppService } from 'src/app/providers/appservice/app.service';

const CONTEXT_PROJECT = 'project';
const CONTEXT_APPLICATION = 'application';
const CONTEXT_SUPPLIER = 'supplier';
const CONTEXT_OTDATA = 'otdata';

@Component({
  standalone: false,
  selector: 'app-mapping-card-popup',
  templateUrl: './mapping-card-popup.component.html',
  styleUrls: ['./mapping-card-popup.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class MappingCardPopupComponent implements OnInit, OnChanges, AfterViewInit {
  @Input() context: 'project' | 'application' | 'supplier' | 'otdata' = CONTEXT_PROJECT;
  @Input() currentSelectedItem: any = {};
  @Input() strategyId: string = '';
  @Input() showButtons: boolean = true;
  @Output() mappingsChange = new EventEmitter<any>();

  public popupConfig: any = {};
  public mappingConfigs: any[] = [];
  public currentActivatedPopupItem: any = {
    title: '',
    blockname: '',
    type: '',
    list: [],
  };
  private dataLoadScheduled: boolean = false;

  constructor(
    private manageDataService: ManagedataService,
    private appService: AppService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    if (this.context) {
      // Initialize configs immediately so UI structure is ready
      this.initializeConfigsForContext();
    }
  }

  ngAfterViewInit(): void {
    // Schedule data loading after view initialization to ensure parent has painted
    if (this.context && !this.dataLoadScheduled) {
      this.scheduleDataLoad();
      this.dataLoadScheduled = true;
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['context'] && changes['context'].currentValue) {
      // Re-initialize configs immediately
      this.initializeConfigsForContext();
      // Reset flag and schedule data load after view update
      this.dataLoadScheduled = false;
      // Use setTimeout to ensure this runs after the change detection cycle
      setTimeout(() => {
        if (!this.dataLoadScheduled) {
          this.scheduleDataLoad();
          this.dataLoadScheduled = true;
        }
      }, 0);
    }
  }

  private scheduleDataLoad(): void {
    // Use setTimeout to defer API calls until after the current rendering cycle
    // This ensures the parent page has fully painted before we start loading data
    // Using requestAnimationFrame ensures it runs after the browser has painted
    requestAnimationFrame(() => {
      setTimeout(() => {
        this.loadDataForContext();
      }, 0);
    });
  }

  private initializeConfigsForContext(): void {
    this.popupConfig = {};
    this.mappingConfigs = [];
    // Initialize only popup configs needed for current context
    if (this.context === CONTEXT_PROJECT) {
      this.popupConfig.dependencyProject = {
        title: 'IS THIS PROJECT DEPENDING ON OTHER PROJECTS?',
        blockname: 'Projects',
        type: 'prgName',
        mapping: 'projects_depending_on',
        list: [],
      };
      this.popupConfig.dependentProject = {
        title: 'ARE OTHER PROJECTS DEPENDENT ON THIS PROJECT?',
        blockname: 'Projects',
        type: 'prgName',
        mapping: 'projects_dependent_on',
        list: [],
      };
      this.popupConfig.application = {
        title: 'RELATED APPLICATIONS?',
        blockname: 'Applications',
        type: 'appName',
        mapping: 'apps',
        list: [],
      };
      this.popupConfig.supplier = {
        title: 'RELATED SUPPLIERS?',
        blockname: 'Suppliers',
        type: 'supplierName',
        mapping: 'suppliers',
        list: [],
      };
      this.popupConfig.otdata = {
        title: 'RELATED OT ASSETS?',
        blockname: 'OT Assets',
        type: 'assetName',
        mapping: 'ot',
        list: [],
      };
      this.popupConfig.hr = {
        title: 'RESOURCES WORKING ON THIS PROJECT',
        blockname: 'Resources',
        type: 'empName',
        mapping: 'resources',
        list: [],
      };
    } else if (this.context === CONTEXT_APPLICATION) {
      this.popupConfig.project = {
        title: 'RELATED PROJECTS?',
        blockname: 'Projects',
        type: 'prgName',
        mapping: 'projects',
        list: [],
      };
      this.popupConfig.supplier = {
        title: 'RELATED SUPPLIERS?',
        blockname: 'Suppliers',
        type: 'supplierName',
        mapping: 'suppliers',
        list: [],
      };
      this.popupConfig.otdata = {
        title: 'RELATED OT ASSETS?',
        blockname: 'OT Assets',
        type: 'assetName',
        mapping: 'ot',
        list: [],
      };
    } else if (this.context === CONTEXT_SUPPLIER) {
      this.popupConfig.project = {
        title: 'RELATED PROJECTS?',
        blockname: 'Projects',
        type: 'prgName',
        mapping: 'projects',
        list: [],
      };
      this.popupConfig.apps = {
        title: 'RELATED APPLICATIONS?',
        blockname: 'Applications',
        type: 'appName',
        mapping: 'apps',
        list: [],
      };
      this.popupConfig.otdata = {
        title: 'RELATED OT ASSETS?',
        blockname: 'OT Assets',
        type: 'assetName',
        mapping: 'ot',
        list: [],
      };
    } else if (this.context === CONTEXT_OTDATA) {
      this.popupConfig.project = {
        title: 'RELATED PROJECTS?',
        blockname: 'Projects',
        type: 'prgName',
        mapping: 'projects',
        list: [],
      };
      this.popupConfig.apps = {
        title: 'RELATED APPLICATIONS?',
        blockname: 'Applications',
        type: 'appName',
        mapping: 'apps',
        list: [],
      };
      this.popupConfig.supplier = {
        title: 'RELATED SUPPLIERS?',
        blockname: 'Suppliers',
        type: 'supplierName',
        mapping: 'suppliers',
        list: [],
      };
    }

    // Set mapping configs based on context
    this.setMappingConfigsForContext();
  }

  private setMappingConfigsForContext(): void {
    // Order: P-A-S-O-H (Projects, Apps, Suppliers, OT, HR)
    // Define configs in the correct order for each context
    const configsByContext: { [key: string]: any[] } = {
      [CONTEXT_PROJECT]: [
        {
          key: 'dependencyProject',
          label: 'IS THIS PROJECT DEPENDING ON OTHER PROJECTS?',
          emptyMessage: 'No projects mapped yet.',
          popupKey: 'dependencyProject'
        },
        {
          key: 'dependentProject',
          label: 'ARE OTHER PROJECTS DEPENDENT ON THIS PROJECT?',
          emptyMessage: 'No projects mapped yet.',
          popupKey: 'dependentProject'
        },
        {
          key: 'application',
          label: 'RELATED APPLICATIONS?',
          emptyMessage: 'No applications mapped yet.',
          popupKey: 'application'
        },
        {
          key: 'supplier',
          label: 'RELATED SUPPLIERS?',
          emptyMessage: 'No suppliers mapped yet.',
          popupKey: 'supplier'
        },
        {
          key: 'otdata',
          label: 'RELATED OT ASSETS?',
          emptyMessage: 'No OT assets mapped yet.',
          popupKey: 'otdata'
        },
        {
          key: 'hr',
          label: 'RESOURCES WORKING ON THIS PROJECT',
          emptyMessage: 'No resources mapped yet.',
          popupKey: 'hr'
        }
      ],
      [CONTEXT_APPLICATION]: [
        {
          key: 'project',
          label: 'RELATED PROJECTS?',
          emptyMessage: 'No projects mapped yet.',
          popupKey: 'project'
        },
        {
          key: 'supplier',
          label: 'RELATED SUPPLIERS?',
          emptyMessage: 'No suppliers mapped yet.',
          popupKey: 'supplier'
        },
        {
          key: 'otdata',
          label: 'RELATED OT ASSETS?',
          emptyMessage: 'No OT assets mapped yet.',
          popupKey: 'otdata'
        }
      ],
      [CONTEXT_SUPPLIER]: [
        {
          key: 'project',
          label: 'RELATED PROJECTS?',
          emptyMessage: 'No projects mapped yet.',
          popupKey: 'project'
        },
        {
          key: 'apps',
          label: 'RELATED APPLICATIONS?',
          emptyMessage: 'No applications mapped yet.',
          popupKey: 'apps'
        },
        {
          key: 'otdata',
          label: 'RELATED OT ASSETS?',
          emptyMessage: 'No OT assets mapped yet.',
          popupKey: 'otdata'
        }
      ],
      [CONTEXT_OTDATA]: [
        {
          key: 'project',
          label: 'RELATED PROJECTS?',
          emptyMessage: 'No projects mapped yet.',
          popupKey: 'project'
        },
        {
          key: 'apps',
          label: 'RELATED APPLICATIONS?',
          emptyMessage: 'No applications mapped yet.',
          popupKey: 'apps'
        },
        {
          key: 'supplier',
          label: 'RELATED SUPPLIERS?',
          emptyMessage: 'No suppliers mapped yet.',
          popupKey: 'supplier'
        }
      ]
    };

    // Get configs for current context (already in correct order)
    this.mappingConfigs = configsByContext[this.context] || [];
  }

  private loadDataForContext(): void {
    const strategyId = this.strategyId || this.appService.currentActivatedFeature.strategyId;
    const handleError = (error: any, dataType: string) => {
      return of([]);
    };

    const calls: any = {
      projects: this.manageDataService.getProjectData(strategyId).pipe(catchError(e => handleError(e, 'projects'))),
    };

    if (this.context !== CONTEXT_APPLICATION) {
      calls.applications = this.manageDataService.getApplicationDataForProject(strategyId).pipe(catchError(e => handleError(e, 'applications')));
    }
    if (this.context !== CONTEXT_SUPPLIER) {
      calls.suppliers = this.manageDataService.getSupplierDataForProject(strategyId).pipe(catchError(e => handleError(e, 'suppliers')));
    }
    if (this.context !== CONTEXT_OTDATA) {
      calls.otdata = this.manageDataService.getOTDataForMapping(strategyId).pipe(catchError(e => handleError(e, 'OT data')));
    }
    if (this.context === CONTEXT_PROJECT) {
      calls.hr = this.manageDataService.getHRDataForProject(strategyId).pipe(catchError(e => handleError(e, 'HR data')));
    }

    forkJoin(calls).subscribe({
      next: (results: any) => {
        // Projects
        if (this.context === CONTEXT_PROJECT) {
          if (this.popupConfig.dependencyProject)
             this.popupConfig.dependencyProject.list = results.projects || [];
          if (this.popupConfig.dependentProject) 
            this.popupConfig.dependentProject.list = results.projects || [];
        } else if (this.popupConfig.project) {
          this.popupConfig.project.list = results.projects || [];
          }

        // Applications
        if (this.context !== CONTEXT_APPLICATION) {
          const configKey = (this.context === CONTEXT_SUPPLIER || this.context === CONTEXT_OTDATA) ? 'apps' : 'application';
          if (this.popupConfig[configKey]) {
            this.popupConfig[configKey].list = results.applications || [];
          }
        }

        // Suppliers
        if (this.context !== CONTEXT_SUPPLIER && this.popupConfig.supplier) {
          this.popupConfig.supplier.list = results.suppliers || [];
        }

        // OT Data
        if (this.context !== CONTEXT_OTDATA && this.popupConfig.otdata) {
          this.popupConfig.otdata.list = results.otdata || [];
        }

        // HR Data
        if (this.context === CONTEXT_PROJECT && this.popupConfig.hr) {
          this.popupConfig.hr.list = results.hr || [];
        }
        this.cdr.detectChanges();
      },
      error: () => {
        this.cdr.markForCheck();
      }
    });
  }

  onOpenPopup(type: any): void {
    this.currentActivatedPopupItem = { ...this.popupConfig[type] };
    if (this.currentActivatedPopupItem && this.popupConfig[type]?.list) {
      // Create a deep copy of the list with all items (no filtering)
      this.currentActivatedPopupItem.list = this.popupConfig[type].list.map((item: any) => ({ ...item }));
      this.updateCheckedStates(type);
    }
  }

  private updateCheckedStates(type: any): void {
    const item = this.currentSelectedItem;
    
    if (!this.currentActivatedPopupItem || !this.currentActivatedPopupItem.list) {
      return;
    }
    
    if (type === 'dependencyProject') {
      const dependentProjectIds = (item.projects_dependent_on || []).map((p: any) => 
        typeof p === 'string' ? p : p._id
      );
      this.currentActivatedPopupItem.list.forEach((x: any) => {
        x.checked = item.projects_depending_on?.find((p: any) => {
          const pId = typeof p === 'string' ? p : p._id;
          return pId === x._id;
        }) ? true : false;
        // Disable items that are already in dependentProject
        x.disabled = dependentProjectIds.includes(x._id);
      });
    } else if (type === 'dependentProject') {
      const dependencyProjectIds = (item.projects_depending_on || []).map((p: any) => 
        typeof p === 'string' ? p : p._id
      );
      this.currentActivatedPopupItem.list.forEach((x: any) => {
        x.checked = item.projects_dependent_on?.find((p: any) => {
          const pId = typeof p === 'string' ? p : p._id;
          return pId === x._id;
        }) ? true : false;
        // Disable items that are already in dependencyProject
        x.disabled = dependencyProjectIds.includes(x._id);
      });
    } else if (type === 'application' || type === 'apps') {
      this.currentActivatedPopupItem.list.forEach((x: any) => {
        // Handle both object arrays and string ID arrays (for OT data context)
        if (this.context === CONTEXT_OTDATA && item.apps && item.apps.length > 0 && typeof item.apps[0] === 'string') {
          x.checked = item.apps.includes(x._id);
        } else {
          x.checked = item.apps?.find((p: any) => (typeof p === 'string' ? p === x._id : p._id === x._id)) ? true : false;
        }
      });
    } else if (type === 'supplier') {
      this.currentActivatedPopupItem.list.forEach((x: any) => {
        // Handle both object arrays and string ID arrays (for OT data context)
        if (this.context === CONTEXT_OTDATA && item.suppliers && item.suppliers.length > 0 && typeof item.suppliers[0] === 'string') {
          x.checked = item.suppliers.includes(x._id);
        } else {
          x.checked = item.suppliers?.find((p: any) => (typeof p === 'string' ? p === x._id : p._id === x._id)) ? true : false;
        }
      });
    } else if (type === 'otdata') {
      this.currentActivatedPopupItem.list.forEach((x: any) => {
        x.checked = item.ot?.find((p: any) => p._id === x._id) ? true : false;
      });
    } else if (type === 'project') {
      this.currentActivatedPopupItem.list.forEach((x: any) => {
        // Handle both object arrays and string ID arrays (for OT data context)
        if (this.context === CONTEXT_OTDATA && item.projects && item.projects.length > 0 && typeof item.projects[0] === 'string') {
          x.checked = item.projects.includes(x._id);
        } else {
          x.checked = item.projects?.find((p: any) => (typeof p === 'string' ? p === x._id : p._id === x._id)) ? true : false;
        }
      });
    } else if (type === 'hr') {
      this.currentActivatedPopupItem.list.forEach((x: any) => {
        x.checked = item.resources?.find((p: any) => p._id === x._id) ? true : false;
      });
    }
  }

  getLabel(item: any, type: any): string {
    return item[type] || '';
  }

  addMappings(): void {
    if (!this.currentActivatedPopupItem || !this.currentActivatedPopupItem.mapping) {
      return;
    }

    let mapping = this.currentActivatedPopupItem.mapping;
    // Map 'otdata' to 'ot' for consistency with model
    if (mapping === 'otdata') {
      mapping = 'ot';
    }
    
    // Filter out disabled items and only get checked items
    const selectedItems = this.currentActivatedPopupItem.list
      .filter((o: any) => o && o.checked === true && !o.disabled)
      .map((item: any) => {
        // For OT data context, if backend expects string IDs, convert to ID only
        if (this.context === CONTEXT_OTDATA && (mapping === 'projects' || mapping === 'apps' || mapping === 'suppliers')) {
          return item._id; // Return just the ID as string
        }
        
        // For other contexts, return full object
        const cleanedItem: any = {};
        if (item._id) cleanedItem._id = item._id;
        if (item.prgName) cleanedItem.prgName = item.prgName;
        if (item.appName) cleanedItem.appName = item.appName;
        if (item.supplierName) cleanedItem.supplierName = item.supplierName;
        if (item.assetName) cleanedItem.assetName = item.assetName;
        if (item.empName) cleanedItem.empName = item.empName;
        // Copy any other properties that might be needed (except UI-only properties)
        Object.keys(item).forEach(key => {
          if (!['checked', 'name'].includes(key) && !cleanedItem.hasOwnProperty(key)) {
            cleanedItem[key] = item[key];
          }
        });
        return cleanedItem;
      });
    
    // Create a new object with a new array reference to ensure change detection
    const updatedItem: any = { ...this.currentSelectedItem };
    updatedItem[mapping] = selectedItems.length > 0 ? [...selectedItems] : []; // Create new array reference
    
    // Emit the change
    this.mappingsChange.emit(updatedItem);
  }

  getMappingItems(key: string): any[] {
    const item = this.currentSelectedItem;
    let rawItems: any[] = [];
    
    switch (key) {
      case 'dependencyProject':
        rawItems = item?.projects_depending_on || [];
        break;
      case 'dependentProject':
        rawItems = item?.projects_dependent_on || [];
        break;
      case 'application':
      case 'apps':
        rawItems = item?.apps || [];
        break;
      case 'supplier':
        rawItems = item?.suppliers || [];
        break;
      case 'otdata':
        rawItems = item?.ot || [];
        break;
      case 'hr':
        rawItems = item?.resources || [];
        break;
      case 'project':
        rawItems = item?.projects || [];
        break;
      default:
        return [];
    }
    
    // If items are string IDs (for OT data context), convert them to objects
    if (this.context === CONTEXT_OTDATA && rawItems.length > 0 && typeof rawItems[0] === 'string') {
      return this.convertIdsToObjects(rawItems, key);
    }
    
    return rawItems;
  }
  
  private convertIdsToObjects(ids: string[], key: string): any[] {
    let sourceList: any[] = [];
    
    // Get the source list from popupConfig based on the key
    switch (key) {
      case 'project':
        sourceList = this.popupConfig.project?.list || [];
        break;
      case 'apps':
        sourceList = this.popupConfig.apps?.list || [];
        break;
      case 'supplier':
        sourceList = this.popupConfig.supplier?.list || [];
        break;
      default:
        return [];
    }
    
    // If source list is not loaded yet, return empty array (will be updated when data loads)
    if (sourceList.length === 0) {
      return [];
    }
    
    // Convert IDs to objects by looking them up in the source list
    return ids
      .map((id: string) => {
        const found = sourceList.find((item: any) => item._id === id);
        if (found) {
          // Return a clean object without UI properties
          const cleanObj: any = { _id: found._id };
          if (found.prgName) cleanObj.prgName = found.prgName;
          if (found.appName) cleanObj.appName = found.appName;
          if (found.supplierName) cleanObj.supplierName = found.supplierName;
          // Copy other relevant properties
          Object.keys(found).forEach(k => {
            if (!['checked', 'name'].includes(k) && !cleanObj.hasOwnProperty(k)) {
              cleanObj[k] = found[k];
            }
          });
          return cleanObj;
        }
        // If not found in list, return a minimal object with just the ID
        // This can happen if the item was deleted but still referenced
        return { _id: id };
      })
      .filter((item): item is any => item !== null);
  }

  getMappingItemName(item: any, key: string): string {
    switch (key) {
      case 'dependencyProject':
      case 'dependentProject':
      case 'project':
        return item.prgName || '';
      case 'application':
      case 'apps':
        return item.appName || '';
      case 'supplier':
        return item.supplierName || '';
      case 'otdata':
        return item.assetName || '';
      case 'hr':
        return item.empName || '';
      default:
        return '';
    }
  }

  hasMappings(key: string): boolean {
    const items = this.getMappingItems(key);
    return items && items.length > 0;
  }

  removeMappingItem(key: string, index: number): void {
    const item = { ...this.currentSelectedItem };
    let arrayToUpdate: any[] | undefined;
    
    switch (key) {
      case 'dependencyProject':
        arrayToUpdate = item.projects_depending_on;
        break;
      case 'dependentProject':
        arrayToUpdate = item.projects_dependent_on;
        break;
      case 'application':
      case 'apps':
        arrayToUpdate = item.apps;
        break;
      case 'supplier':
        arrayToUpdate = item.suppliers;
        break;
      case 'otdata':
        arrayToUpdate = item.ot;
        break;
      case 'hr':
        arrayToUpdate = item.resources;
        break;
      case 'project':
        arrayToUpdate = item.projects;
        break;
    }
    
    // Create new array reference for change detection
    if (arrayToUpdate && arrayToUpdate.length > index) {
      const newArray = [...arrayToUpdate];
      newArray.splice(index, 1);
      
      switch (key) {
        case 'dependencyProject':
          item.projects_depending_on = newArray;
          break;
        case 'dependentProject':
          item.projects_dependent_on = newArray;
          break;
        case 'application':
        case 'apps':
          item.apps = newArray;
          break;
        case 'supplier':
          item.suppliers = newArray;
          break;
        case 'otdata':
          item.ot = newArray;
          break;
        case 'hr':
          item.resources = newArray;
          break;
        case 'project':
          item.projects = newArray;
          break;
      }
    }
    
    this.mappingsChange.emit(item);
  }
}


